import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { Link } from 'react-router-dom';
import {
  Users, Truck, Camera, AlertTriangle, Calendar, Clock,
  CheckCircle, XCircle, ChevronRight, UserPlus, Eye, Image, Bell,
  Play, Mail, Send, Timer, AlertCircle, Package
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const PHOTO_LABELS = {
  lateral_left: 'Latéral G',
  lateral_right: 'Latéral D',
  front: 'Avant',
  back: 'Arrière',
  dashboard: 'Tableau'
};

const AdminDashboard = ({ user, activeModule = 'b2b' }) => {
  const [dashboard, setDashboard] = useState(null);
  const [photoStatus, setPhotoStatus] = useState([]);
  const [vehicleAlerts, setVehicleAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [unclosedMissions, setUnclosedMissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeePhotos, setEmployeePhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => {
    fetchDashboard();
    // Rafraîchir les missions non clôturées toutes les 2 minutes
    const interval = setInterval(fetchUnclosedMissions, 120000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnclosedMissions = async () => {
    try {
      const response = await axios.get(`${API}/missions/unclosed`, { withCredentials: true });
      setUnclosedMissions(response.data);
    } catch (error) {
      console.error('Failed to fetch unclosed missions:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const [dashRes, photoRes, alertsRes, notifRes, unclosedRes] = await Promise.all([
        axios.get(`${API}/dashboard/admin`, { withCredentials: true }),
        axios.get(`${API}/photos/admin/all-employees`, { withCredentials: true }),
        axios.get(`${API}/vehicles/alerts`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/missions/unclosed`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setDashboard(dashRes.data);
      setPhotoStatus(photoRes.data);
      setVehicleAlerts(alertsRes.data);
      // Handle notifications - API already filtered by role/site scope
      const notifData = Array.isArray(notifRes.data) ? notifRes.data : (notifRes.data?.notifications || []);
      const activeNotifications = notifData.filter((n) => n.status !== 'processed' && n.status !== 'archived');
      setNotifications(activeNotifications.slice(0, 5));
      setUnreadNotificationsCount(notifRes.data?.unread_count || activeNotifications.filter((n) => !n.read).length || 0);
      setUnclosedMissions(unclosedRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendReminderEmail = async (userId, userName) => {
    if (sendingReminder === userId) return;
    setSendingReminder(userId);
    try {
      await axios.post(`${API}/missions/unclosed/${userId}/send-reminder`, {}, { withCredentials: true });
      alert(`✅ Email de rappel envoyé à ${userName}`);
      // Rafraîchir la liste
      fetchUnclosedMissions();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      alert(`❌ ${message}`);
    } finally {
      setSendingReminder(null);
    }
  };

  const dismissMission = async (userId, userName) => {
    if (!window.confirm(`Masquer la mission de ${userName} du tableau de bord ?`)) return;
    try {
      await axios.delete(`${API}/missions/unclosed/${userId}/dismiss`, { withCredentials: true });
      // Rafraîchir la liste
      fetchUnclosedMissions();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      alert(`❌ ${message}`);
    }
  };

  const viewEmployeePhotos = async (userId, userName) => {
    setSelectedEmployee({ user_id: userId, name: userName });
    setLoadingPhotos(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/photos/admin/employee/${userId}?date=${today}`, {
        withCredentials: true
      });
      setEmployeePhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      setEmployeePhotos([]);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`, {}, { withCredentials: true });
      fetchDashboard();
    } catch (error) {
      console.error('Failed to approve user:', error);
      toast.error(`Approbation non enregistrée : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/reject`, {}, { withCredentials: true });
      fetchDashboard();
    } catch (error) {
      console.error('Failed to reject user:', error);
      toast.error(`Refus non enregistré : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get all employees with complete photos today
  const employeesWithPhotos = photoStatus.filter(p => p.start_complete || p.end_complete);
  const employeesWithoutPhotos = photoStatus.filter(p => !p.start_complete && !p.end_complete);

  return (
    <Layout user={user} activeModule={activeModule}>
      <div data-testid="admin-dashboard" className="space-y-6 fade-in">
        {/* Site Badge for CP Admins */}
        {user?.admin_level === 'colis_prive' && user?.assigned_site_ids?.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 border border-orange-200 rounded-xl" data-testid="site-badge">
            <Package className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-semibold text-orange-800">
              Site : {user.assigned_site_ids.length === 1 ? 'Colis Privé' : `${user.assigned_site_ids.length} sites`}
            </span>
            <span className="text-xs text-orange-600 ml-1">(Données filtrées pour votre site uniquement)</span>
          </div>
        )}

        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0F172A] font-['Chivo']">Tableau de bord B2B</h1>
          <p className="text-gray-500 capitalize">{today}</p>
        </div>

        {/* Stats Grid - Cliquables */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/b2b/employees">
            <Card className="stat-card hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#0F172A]">Salariés</p>
                    <p className="text-xs text-gray-500">Gestion des employés</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/b2b/vehicles">
            <Card className="stat-card hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Truck className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0F172A]">{dashboard?.vehicles?.total || 0}</p>
                    <p className="text-xs text-gray-500">Véhicules</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/b2b/planning">
            <Card className="stat-card border-green-200 bg-green-50 hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-green-700">Planning</p>
                    <p className="text-xs text-green-600">Gestion du planning</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/formations-binome">
            <Card className="stat-card border-blue-200 bg-blue-50 hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-bold text-blue-700 leading-tight">Formation</p>
                    <p className="text-xs text-blue-600">et RDV</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/notifications">
            <Card className="stat-card border-orange-200 bg-orange-50 hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Bell className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-bold text-orange-700 leading-tight">{unreadNotificationsCount}</p>
                    <p className="text-xs text-orange-600">Notifications</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* MISSIONS NON CLÔTURÉES - Section prioritaire - TOUJOURS VISIBLE */}
        <Card className={`border-2 ${
          unclosedMissions?.over_4h_count > 0 
            ? 'border-red-400 bg-gradient-to-r from-red-50 to-orange-50' 
            : unclosedMissions?.total_unclosed > 0
              ? 'border-orange-400 bg-gradient-to-r from-orange-50 to-amber-50'
              : 'border-green-300 bg-gradient-to-r from-green-50 to-emerald-50'
        }`} data-testid="unclosed-missions-panel">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  unclosedMissions?.over_4h_count > 0 
                    ? 'bg-red-500 animate-pulse' 
                    : unclosedMissions?.total_unclosed > 0
                      ? 'bg-orange-500 animate-pulse'
                      : 'bg-green-500'
                }`}>
                  <Timer className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className={`font-bold ${
                    unclosedMissions?.total_unclosed > 0 ? 'text-orange-800' : 'text-green-800'
                  }`}>
                    {unclosedMissions?.total_unclosed > 0 
                      ? 'Missions en cours non clôturées' 
                      : 'Missions en cours'}
                  </span>
                  <p className={`text-sm font-normal ${
                    unclosedMissions?.total_unclosed > 0 ? 'text-orange-600' : 'text-green-600'
                  }`}>
                    {unclosedMissions?.total_unclosed || 0} mission(s) en attente de clôture
                    {unclosedMissions?.over_4h_count > 0 && (
                      <span className="text-red-600 font-semibold ml-1">
                        • {unclosedMissions.over_4h_count} depuis +4h ⚠️
                      </span>
                    )}
                  </p>
                </div>
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchUnclosedMissions}
                className="border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {unclosedMissions?.missions?.length > 0 ? (
              <div className="space-y-3">
                {unclosedMissions.missions.map((mission) => (
                  <div 
                    key={mission.user_id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      mission.is_over_4h 
                        ? 'bg-red-50 border-red-300' 
                        : 'bg-white border-gray-200'
                    }`}
                    data-testid={`unclosed-mission-${mission.user_id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        mission.is_over_4h ? 'bg-red-500' : 'bg-blue-500'
                      }`}>
                        <Play className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{mission.user_name}</p>
                        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Début: {mission.start_time}
                          </span>
                          <span className={`font-semibold ${mission.is_over_4h ? 'text-red-600' : 'text-orange-600'}`}>
                            {mission.elapsed_hours}h écoulées
                          </span>
                          {mission.site_name && mission.site_name !== 'N/A' && (
                            <span className="text-gray-400">• {mission.site_name}</span>
                          )}
                          {mission.reminder_count > 0 && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                              {mission.reminder_count} rappel{mission.reminder_count > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {/* Bouton Rappel - toujours disponible */}
                      <Button
                        size="sm"
                        onClick={() => sendReminderEmail(mission.user_id, mission.user_name)}
                        disabled={sendingReminder === mission.user_id}
                        className={`${
                          mission.is_over_4h 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-orange-600 hover:bg-orange-700'
                        } text-white`}
                        data-testid={`send-reminder-${mission.user_id}`}
                      >
                        {sendingReminder === mission.user_id ? (
                          <span className="flex items-center gap-1">
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                            Envoi...
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Send className="w-4 h-4" />
                            Rappel
                          </span>
                        )}
                      </Button>
                      {/* Bouton Supprimer */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => dismissMission(mission.user_id, mission.user_name)}
                        className="border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        data-testid={`dismiss-mission-${mission.user_id}`}
                        title="Masquer cette mission"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <p className="font-medium text-green-700">Aucune mission en attente de clôture</p>
                <p className="text-sm text-gray-400 mt-1">Toutes les missions du jour sont clôturées</p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-3 text-center">
              Dernière mise à jour: {unclosedMissions?.current_time || '--:--'} • Actualisation auto toutes les 2 min
            </p>
          </CardContent>
        </Card>

        {/* ALERTES VÉHICULES - En haut pour visibilité immédiate */}
        {vehicleAlerts.length > 0 && (
          <Card className={`${
            vehicleAlerts.some(a => a.severity === 'critical') 
              ? 'border-red-300 bg-red-50 border-2' 
              : 'border-orange-300 bg-orange-50 border-2'
          }`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    vehicleAlerts.some(a => a.severity === 'critical') 
                      ? 'bg-red-500 animate-pulse' 
                      : 'bg-orange-500'
                  }`}>
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span className={`text-lg ${
                      vehicleAlerts.some(a => a.severity === 'critical') ? 'text-red-700' : 'text-orange-700'
                    }`}>
                      ⚠️ Alertes Véhicules
                    </span>
                    <p className="text-sm font-normal text-gray-600">
                      {vehicleAlerts.filter(a => a.severity === 'critical').length} expiré(s), 
                      {' '}{vehicleAlerts.filter(a => a.severity !== 'critical').length} proche(s) échéance
                    </p>
                  </div>
                </CardTitle>
                <Link to="/admin/vehicle-management">
                  <Button className="btn-primary">
                    Gérer les véhicules <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {vehicleAlerts.map((alert, index) => (
                  <Link 
                    key={index} 
                    to={`/admin/vehicles/${alert.vehicle_id}/details`}
                    className="block"
                  >
                    <div className={`p-4 rounded-lg border-2 transition-all hover:shadow-md cursor-pointer ${
                      alert.severity === 'critical' 
                        ? 'bg-red-100 border-red-300 hover:border-red-500' 
                        : alert.severity === 'urgent'
                          ? 'bg-orange-100 border-orange-300 hover:border-orange-500'
                          : 'bg-yellow-100 border-yellow-300 hover:border-yellow-500'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          alert.severity === 'critical' ? 'bg-red-500' :
                          alert.severity === 'urgent' ? 'bg-orange-500' : 'bg-yellow-500'
                        }`}>
                          <Truck className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-bold text-gray-900">{alert.plate_number}</p>
                            <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                              alert.severity === 'critical' 
                                ? 'bg-red-500 text-white' 
                                : alert.severity === 'urgent'
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-yellow-500 text-white'
                            }`}>
                              {alert.severity === 'critical' ? '⛔ EXPIRÉ' :
                               alert.severity === 'urgent' ? '⚠️ URGENT' : '⏰ BIENTÔT'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{alert.brand} {alert.model}</p>
                          <p className={`text-sm font-medium mt-2 ${
                            alert.severity === 'critical' ? 'text-red-700' :
                            alert.severity === 'urgent' ? 'text-orange-700' : 'text-yellow-700'
                          }`}>
                            {alert.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Échéance: {new Date(alert.date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notifications récentes */}
        {notifications.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notifications récentes
                </CardTitle>
                <Link to="/notifications">
                  <Button variant="outline" size="sm" data-testid="view-all-notifications-btn">
                    Voir toutes
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {notifications.slice(0, 3).map((notification) => (
                  <div
                    key={notification.notification_id}
                    className={`p-3 rounded-lg border ${!notification.read ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{notification.title}</p>
                        <p className="text-gray-600 text-xs mt-1">{notification.message}</p>
                        <p className="text-gray-400 text-xs mt-2">
                          {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Demandes en attente */}
        {dashboard?.users?.pending > 0 && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <Link to="/admin/b2b/employees">
                <div className="flex items-center justify-between cursor-pointer hover:opacity-80">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    <div>
                      <p className="font-bold text-yellow-900">{dashboard.users.pending} demande(s) en attente</p>
                      <p className="text-sm text-yellow-700">Cliquez pour approuver ou rejeter</p>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-yellow-600" />
                </div>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Section supprimée - photos maintenant dans l'onglet véhicules */}
        <Card className="hidden">
          <CardContent className="p-4">
            {photoStatus.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Aucun salarié enregistré</p>
            ) : (
              <div className="space-y-3 hidden">
                {photoStatus.map((emp) => (
                  <div 
                    key={emp.user.user_id} 
                    className="hidden"
                    onClick={() => viewEmployeePhotos(emp.user.user_id, emp.user.name)}
                  >
                    <div className="flex items-center gap-3">
                      {emp.user.picture ? (
                        <img src={emp.user.picture} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 bg-[#0F172A] rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">{emp.user.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#0F172A]">{emp.user.name}</p>
                        <div className="flex gap-3 text-sm">
                          <span className={emp.start_complete ? 'text-green-600' : 'text-red-600'}>
                            Début: {emp.start_complete ? '✅ 5/5' : `❌ ${emp.start_photos?.length || 0}/5`}
                          </span>
                          <span className={emp.end_complete ? 'text-green-600' : 'text-red-600'}>
                            Fin: {emp.end_complete ? '✅ 5/5' : `❌ ${emp.end_photos?.length || 0}/5`}
                          </span>
                        </div>
                        {emp.vehicle_plate && (
                          <p className="text-xs text-gray-500 font-mono">🚗 {emp.vehicle_plate}</p>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4 mr-1" /> Voir
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div>
          {/* Pending Approvals */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Inscriptions en attente
                </CardTitle>
                <Link to="/admin/b2b/employees">
                  <Button variant="ghost" size="sm">
                    Voir tout <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {dashboard?.pending_approvals?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.pending_approvals.slice(0, 5).map((pendingUser) => (
                    <div key={pendingUser.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {pendingUser.picture ? (
                          <img src={pendingUser.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 bg-[#0F172A] rounded-full flex items-center justify-center">
                            <span className="text-white font-medium">{pendingUser.name?.charAt(0)}</span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-[#0F172A]">{pendingUser.name}</p>
                          <p className="text-sm text-gray-500">{pendingUser.email}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" className="text-green-600 hover:bg-green-50" onClick={() => handleApprove(pendingUser.user_id)}>
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleReject(pendingUser.user_id)}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">Aucune inscription en attente</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Photo Detail Dialog */}
        <Dialog open={!!selectedEmployee} onOpenChange={() => setSelectedEmployee(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Photos de {selectedEmployee?.name} - Aujourd'hui</DialogTitle>
            </DialogHeader>
            {loadingPhotos ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F172A]"></div>
              </div>
            ) : employeePhotos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune photo pour aujourd'hui</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Start photos */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-[#0F172A] flex items-center gap-2">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    Début de service
                    {employeePhotos.filter(p => p.session_type === 'start').length > 0 && (
                      <span className="text-sm font-normal text-gray-500">
                        - Plaque: {employeePhotos.find(p => p.session_type === 'start')?.vehicle_plate}
                      </span>
                    )}
                  </h3>
                  <div className="grid grid-cols-5 gap-2">
                    {['lateral_left', 'lateral_right', 'front', 'back', 'dashboard'].map(type => {
                      const photo = employeePhotos.find(p => p.session_type === 'start' && p.photo_type === type);
                      return (
                        <div key={type} className="text-center">
                          {photo ? (
                            <img src={photo.image_url || photo.image_base64} alt={type} className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-90" onClick={() => window.open(photo.image_url || photo.image_base64, '_blank')} />
                          ) : (
                            <div className="w-full aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                              <Camera className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">{PHOTO_LABELS[type]}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* End photos */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-[#0F172A] flex items-center gap-2">
                    <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                    Fin de service
                    {employeePhotos.filter(p => p.session_type === 'end').length > 0 && (
                      <span className="text-sm font-normal text-gray-500">
                        - Plaque: {employeePhotos.find(p => p.session_type === 'end')?.vehicle_plate}
                      </span>
                    )}
                  </h3>
                  <div className="grid grid-cols-5 gap-2">
                    {['lateral_left', 'lateral_right', 'front', 'back', 'dashboard'].map(type => {
                      const photo = employeePhotos.find(p => p.session_type === 'end' && p.photo_type === type);
                      return (
                        <div key={type} className="text-center">
                          {photo ? (
                            <img src={photo.image_url || photo.image_base64} alt={type} className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-90" onClick={() => window.open(photo.image_url || photo.image_base64, '_blank')} />
                          ) : (
                            <div className="w-full aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                              <Camera className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">{PHOTO_LABELS[type]}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
